import Divider from "@components/Divider/Divider";
import Link from "@components/Link";
import { ThemeEnum } from "@components/ThemeProvider/ThemeProvider";
import { faArrowLeft, faArrowRight } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import clsx from "clsx";

type SubHeaderProps = {
  title: string;
  href?: string;
  noIcon?: boolean;
  theme?: ThemeEnum;
  showDivider?: boolean;
  onPrev?: () => void;
  onNext?: () => void;
  disabledPrev?: boolean;
  disabledNext?: boolean;
  className?: string;
  iconSize?: number;
};

export default function SubHeader({
  title,
  href,
  noIcon = false,
  theme = ThemeEnum.light,
  showDivider = true,
  onPrev,
  onNext,
  disabledPrev = false,
  disabledNext = false,
  className,
  iconSize = 24,
}: SubHeaderProps) {
  const isDark = theme === ThemeEnum.dark;
  const iconGlyphSize = Math.round(iconSize / 2);

  const icon = !noIcon && (
    <div
      className="bg-button-primary flex shrink-0 items-center justify-center rounded-full"
      style={{ width: iconSize, height: iconSize }}
    >
      <FontAwesomeIcon
        icon={faArrowRight}
        className="text-white"
        style={{ width: iconGlyphSize, height: iconGlyphSize }}
      />
    </div>
  );

  return (
    <div className={clsx("flex w-full flex-col", className)}>
      <div
        className={clsx(
          "flex w-full items-center justify-between px-3 py-4 pb-2",
          isDark && "bg-background-primary-on-dark",
        )}
      >
        <h3
          className={clsx(
            "text-header-3 flex shrink-0 items-center gap-2",
            isDark ? "text-primary-on-dark" : "text-primary",
          )}
        >
          {href ? (
            <Link
              to={href}
              aria-label={title}
              className={clsx(
                "group inline-flex cursor-pointer items-center gap-3 no-underline",
                isDark && "hover:text-link-video-hover",
              )}
            >
              {title}
              {icon}
            </Link>
          ) : (
            <span className="inline-flex items-center gap-3">
              {title}
              {icon}
            </span>
          )}
        </h3>

        <div className="flex items-center gap-2">
          {onPrev && (
            <button
              onClick={onPrev}
              disabled={disabledPrev}
              className={clsx(
                "flex size-6 shrink-0 cursor-pointer items-center justify-center",
                disabledPrev
                  ? "text-tertiary cursor-not-allowed"
                  : isDark
                    ? "text-primary-on-dark"
                    : "text-primary",
              )}
              aria-label="Previous"
            >
              <FontAwesomeIcon icon={faArrowLeft} className="size-4" />
            </button>
          )}
          {onNext && (
            <button
              onClick={onNext}
              disabled={disabledNext}
              className={clsx(
                "flex size-6 shrink-0 cursor-pointer items-center justify-center",
                disabledNext
                  ? "text-tertiary cursor-not-allowed"
                  : isDark
                    ? "text-primary-on-dark"
                    : "text-primary",
              )}
              aria-label="Next"
            >
              <FontAwesomeIcon icon={faArrowRight} className="size-4" />
            </button>
          )}
        </div>
      </div>

      {showDivider && (
        <div className="px-3">
          <Divider className="my-0!" />
        </div>
      )}
    </div>
  );
}
